import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Dashboard } from 'types/dashboard';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';

import { migrateDataWidgetFilters } from '../components/DataWidgetFiltersInput/migrate';
import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useHasAccess } from 'hooks/useHasAccess';

export type UseDashboardsFilterParams = OffsetPaginationParams & {
  dashboard_ids?: Dashboard['id'][];
  dashboard_names?: Dashboard['name'][];
};

export type UseDashboardsOptions = UseQueryOptions<
  OffsetPaginated<Dashboard>,
  APIError
>;

export function useDashboards(
  {
    pagination = 'offset',
    limit = 50,
    page = 1,
    ...filters
  }: UseDashboardsFilterParams = {},
  { enabled, ...options }: UseDashboardsOptions = {}
) {
  const { applicationID, client } = useAPI();
  const params = {
    pagination,
    limit,
    page,
    ...filters,
  };
  const [hasAccess] = useHasAccess();

  return useAuthenticatedQuery(
    ['dashboards', applicationID, params],
    async () => {
      const { data } = await client.get<OffsetPaginated<Dashboard>>(
        `/internal/apps/${applicationID}/dashboards`,
        {
          params,
        }
      );

      data.data = data.data.map((dashboard) =>
        migrateDataWidgetFilters(dashboard)
      );

      return data;
    },
    {
      ...options,
      enabled:
        enabled !== undefined
          ? enabled && hasAccess('monitor')
          : hasAccess('monitor'),
    }
  );
}
